import { cloneDeep } from 'lodash';
import {
  ScratchpadRecommendation,
  useGetScratchpadRecommendationLazyQuery,
} from '../../../../../../../api/graphql/generated';
import { getDifferentKeyPaths } from '../../../../../../../util/object';
import { WorkPiecePositionName } from '../../../../../../../util/part.util';
import { ApplicationDefaultParameterSetLocalFormikFormValues } from '../../../../../shared/component/settings/application-settings-edit';
import { useFlowServices } from '../../../../../shared/hook/flow-service.hooks';
import {
  Flow,
  FlowItem,
  FlowItemForm,
  SystemUnit,
  WidgetType,
} from '../../../../../shared/model/flow.types';
import {
  FlowItemService,
  FlowService,
} from '../../../../../shared/service/service.types';
import { getParameterValue } from '../../../../../shared/util/flow-parameter.util';
import { BeaconWeldingData } from '../../../model/beacon-welding-data';
import { WeldSettingsRecommendation } from './settings-recommendation.hook';

const RECOMMENDATION_CONFIG_INPUTS = [
  'materialType',
  'materialThickness',
  'wireType',
  'wireSize', // same as the wireDiameter
  'gas',
];

const RECOMMENDATION_PARAM_INPUTS: BeaconWeldingData.FlowItemParameterRef[] = [
  BeaconWeldingData.FlowItemParameterRef.PROCESS,
  BeaconWeldingData.FlowItemParameterRef.WIRE_FEED_SPEED,
  BeaconWeldingData.FlowItemParameterRef.VOLTAGE,
];

// Parameters that should trigger new recommendations when changed
// Only includes PROCESS and WEAVE_ENABLED - excludes WIRE_FEED_SPEED and VOLTAGE to allow fine-tuning without re-fetching
const RECOMMENDATION_TRIGGER_PARAM_INPUTS: BeaconWeldingData.FlowItemParameterRef[] =
  [
    BeaconWeldingData.FlowItemParameterRef.PROCESS,
    BeaconWeldingData.FlowItemParameterRef.WEAVE_ENABLED,
  ];

const getPositionalSettingsRecommendation = (
  recommendation: ScratchpadRecommendation,
  position: WorkPiecePositionName
): WeldSettingsRecommendation | undefined => {
  const recommendations: WeldSettingsRecommendation[] =
    recommendation.outputs.similar_settings || [];

  return recommendations.find(
    (recommendation) => recommendation.weldPosition === position
  );
};

/**
 * This will find if there are differences between two forms by using the passed
 * in flow item parameters and tags. It will return if it meets one
 * of the criteria.
 */
function findFormDifferences<T extends Record<string, unknown> = FlowItemForm>(
  previousForm: T,
  updatedForm: T,
  params?: BeaconWeldingData.FlowItemParameterRef[],
  tags?: string[]
): boolean {
  const diff = getDifferentKeyPaths(previousForm, updatedForm);
  let isDifferent = false;

  // check if the changed value should trigger a recommendation
  for (const path of diff) {
    if (params) {
      // the path in the diff should contain one of the setting or param names
      const matchedParam = params.find((param) => {
        // we're adding "value" to the path to only check if the value changed
        // not the "originalValue"
        const pattern = new RegExp(`${param}.value`, 'g');
        const match = path.match(pattern);
        return match !== null;
      });

      if (matchedParam) {
        isDifferent = true;
        break;
      }
    }

    if (tags) {
      const matchedConfigKey = tags.find((key) => {
        const pattern = new RegExp(key, 'g');
        const match = path.match(pattern);
        return match !== null;
      });

      if (matchedConfigKey) {
        isDifferent = true;
        break;
      }
    }
  }

  return isDifferent;
}

interface IsRecommendationInputEqualToOptions {
  flowService: FlowService<BeaconWeldingData.FlowConfig>;
  flowItemService: FlowItemService<BeaconWeldingData.FlowConfig>;
  recommendation: ScratchpadRecommendation;
  settingsForm: ApplicationDefaultParameterSetLocalFormikFormValues;
  system: SystemUnit;
}

const compareRecommendationInput = (
  options: IsRecommendationInputEqualToOptions
) => {
  const { flowService, flowItemService, recommendation, settingsForm, system } =
    options;

  const incomingRecommendationForm = flowItemService.convertFlowItemToForm(
    recommendation.inputs.flowItem,
    system
  );

  const incomingRecommendationTags =
    flowService.mapFlowConfigToApplicationDefaultParameterSetFormTags({
      config: recommendation.inputs.flowConfig as BeaconWeldingData.FlowConfig,
    } as Flow<BeaconWeldingData.FlowConfig>);

  const convertedIncomingRecommendationForm =
    convertFormParametersToNumbersFromString(
      incomingRecommendationForm,
      flowItemService
    );

  const convertedSettingsFlowItemForm =
    convertFormParametersToNumbersFromString(
      settingsForm.data,
      flowItemService
    );

  const incomingRecommendationInputDifferent =
    findFormDifferences<ApplicationDefaultParameterSetLocalFormikFormValues>(
      {
        data: convertedIncomingRecommendationForm,
        tags: incomingRecommendationTags,
      },
      { data: convertedSettingsFlowItemForm, tags: settingsForm.tags },
      RECOMMENDATION_PARAM_INPUTS,
      RECOMMENDATION_CONFIG_INPUTS
    );

  return !incomingRecommendationInputDifferent;
};

// forms require the numeric parameters to be strings...
// so this function clones the form and returns them back
// to numbers
const convertFormParametersToNumbersFromString = (
  form: FlowItemForm,
  flowItemService: FlowItemService<BeaconWeldingData.FlowConfig>
) => {
  const convertedForm = cloneDeep(form);
  for (const [k, v] of Object.entries(convertedForm.parameters)) {
    if (
      Object.values<string>(BeaconWeldingData.FlowItemParameterRef).includes(k)
    ) {
      const metadata = flowItemService.getParameterMetadata(
        BeaconWeldingData.FlowItemRef.WELD,
        k as BeaconWeldingData.FlowItemParameterRef
      );

      const metaTypeIsNumber =
        metadata && metadata.value.uom && metadata.value.type === 'number';

      const widgetTypeIsNumber =
        metadata?.ui?.widget && metadata.ui.widget === WidgetType.NUMBER;

      if (metaTypeIsNumber || widgetTypeIsNumber) {
        try {
          const convertedValue = Number(v.value);
          if (!isNaN(convertedValue)) {
            convertedForm.parameters[k].value = convertedValue;
          }
        } catch {
          // if there's an error converting for whatever reason, leave alone
          convertedForm.parameters[k].value = v.value;
        }
      }
    }
  }

  return convertedForm;
};

interface IsRecommendationUtilizedOptions {
  recommendation: WeldSettingsRecommendation;
  flowItem: FlowItem;
}

const isRecommendationUtilized = (
  options: IsRecommendationUtilizedOptions
): boolean => {
  const { recommendation, flowItem } = options;

  let isUtilized = false;

  const unusedRecommendation = recommendation.parameters.find(
    (recommendedParam) => {
      const actualParam = getParameterValue<number>(
        flowItem,
        recommendedParam.ref
      );

      isUtilized =
        actualParam < recommendedParam.maxValue &&
        actualParam > recommendedParam.minValue;
      return !isUtilized;
    }
  );

  return unusedRecommendation === undefined;
};

interface GetFlowRecommendationOptions {
  settingsForm: ApplicationDefaultParameterSetLocalFormikFormValues;
  recommendationId?: string;
}

type GetFlowRecommendationReturnType = (
  options: GetFlowRecommendationOptions
) => Promise<Flow['recommendation'] | undefined>;

const useGetFlowRecommendation = (): GetFlowRecommendationReturnType => {
  const { flowItemService, flow, system, flowService } =
    useFlowServices<BeaconWeldingData.FlowConfig>();

  const [getScratchpadRecommendation] = useGetScratchpadRecommendationLazyQuery(
    {
      fetchPolicy: 'cache-first',
    }
  );

  const getFlowRecommendation = async (
    options: GetFlowRecommendationOptions
  ): Promise<Flow['recommendation'] | undefined> => {
    const { recommendationId, settingsForm } = options;
    const { data } = settingsForm;
    const item = flowItemService.convertFormToFlowItem(data, system);

    const existingScratchpadRecommendation = flow?.recommendation?.id
      ? await getScratchpadRecommendation({
          variables: {
            input: {
              id: flow.recommendation.id,
            },
          },
        })
      : undefined;

    const existingRecommendation =
      existingScratchpadRecommendation?.data?.getScratchpadRecommendation;

    const existingHorizontalRecommendation = existingRecommendation
      ? getPositionalSettingsRecommendation(
          existingRecommendation,
          WorkPiecePositionName.HORIZONTAL
        )
      : undefined;

    const existingRecommendationInputEqual = existingRecommendation
      ? compareRecommendationInput({
          flowService,
          flowItemService,
          recommendation: existingRecommendation,
          settingsForm,
          system,
        })
      : false;

    const incomingScratchpadRecommendation = recommendationId
      ? await getScratchpadRecommendation({
          variables: {
            input: {
              id: recommendationId,
            },
          },
        })
      : undefined;

    const incomingRecommendation =
      incomingScratchpadRecommendation?.data?.getScratchpadRecommendation;

    const incomingHorizontalRecommendation = incomingRecommendation
      ? getPositionalSettingsRecommendation(
          incomingRecommendation,
          WorkPiecePositionName.HORIZONTAL
        )
      : undefined;

    const incomingRecommendationInputEqual = incomingRecommendation
      ? compareRecommendationInput({
          flowService,
          flowItemService,
          recommendation: incomingRecommendation,
          settingsForm,
          system,
        })
      : false;

    // evaluate the incoming recommendation first
    if (incomingRecommendationInputEqual && incomingHorizontalRecommendation) {
      // compare the values
      const utilized = isRecommendationUtilized({
        recommendation: incomingHorizontalRecommendation,
        flowItem: item,
      });

      return {
        id: incomingRecommendation?.id as string,
        utilized,
      };
    } else if (
      existingRecommendationInputEqual &&
      existingHorizontalRecommendation
    ) {
      const utilized = isRecommendationUtilized({
        recommendation: existingHorizontalRecommendation,
        flowItem: item,
      });
      return {
        id: existingRecommendation?.id as string,
        utilized,
      };
    } else {
      return;
    }
  };

  return getFlowRecommendation;
};

export {
  RECOMMENDATION_CONFIG_INPUTS,
  RECOMMENDATION_PARAM_INPUTS,
  RECOMMENDATION_TRIGGER_PARAM_INPUTS,
  compareRecommendationInput,
  convertFormParametersToNumbersFromString,
  findFormDifferences,
  getPositionalSettingsRecommendation,
  isRecommendationUtilized,
  useGetFlowRecommendation,
};
